import { StudentStatusBadge } from '@/components/students/student-status-badge';
import { EntityTable } from '@/components/table/entity-table';
import { TableCellWithPopover } from '@/components/table/table-cell-with-popover';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyArray, getSchoolPortalStudents } from '@/lib/api-calls';
import { buildClientExportColumns, exportToCSV } from '@/lib/csv-utils';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { useNavigateSearchState } from '@/lib/routing';
import { useSorting } from '@/lib/sorting';
import {
  calculateAttendanceRate,
  formatAttendedSessionDatesFromFeedback,
  getStudentStatusLabel,
  getStudentStatusOptions,
  getStudentYearOptions,
} from '@/lib/student-utils';
import { airColumnHelper } from '@/lib/tables';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { CsvCellValue } from '@airreading/common';
import {
  allPagesModifier,
  getSchoolPortalStudentIdDisplay,
  GetSchoolPortalStudentsSortableColumn,
  getSchoolPortalStudentsSortableColumnSchema,
  SchoolPortalStudent,
  StudentStatus,
  StudentYearColorMap,
  studentYearOption,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { LoaderIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';

const schoolStudentsRoute = getRouteApi('/_layout/school-students/');

const columnHelper = airColumnHelper<SchoolPortalStudent>(getSchoolPortalStudentsSortableColumnSchema);

const dualLineHeader = (title: string, subtitle: string) => (
  <div className="flex flex-col">
    <span>{title}</span>
    <span className="text-xs font-normal text-muted-foreground">{subtitle}</span>
  </div>
);

const dataColumns = [
  columnHelper.accessor('firstName', {
    id: 'firstName',
    header: 'First name',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('lastName', {
    id: 'lastName',
    header: 'Last name',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? <StudentStatusBadge status={status} /> : '-';
    },
  }),
  columnHelper.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
          {studentYearOption(grade)}
        </span>
      );
    },
  }),
  columnHelper.accessor('requestedClassTime', {
    id: 'requestedClassTime',
    header: 'Class time',
    cell: ({ getValue }) => {
      const classTime = getValue();
      if (!classTime) return '-';
      return <Badge variant="outline">{formatStudentClassTimeAbbr(classTime)}</Badge>;
    },
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendedSessionsOverall',
    header: () => dualLineHeader('Attended sessions', 'Overall'),
    cell: ({ getValue }) => {
      const sessionFeedbacks = getValue();
      return (
        sessionFeedbacks?.filter((feedback) => feedback.studentAttended === true && feedback.status === 'completed')
          ?.length ?? 0
      );
    },
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendancePercentOverall',
    header: () => dualLineHeader('Attendance %', 'Overall'),
    cell: ({ getValue }) => `${calculateAttendanceRate(getValue())}%`,
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendancePercentPast14Days',
    header: () => dualLineHeader('Attendance %', 'Past 14 days'),
    cell: ({ getValue }) => `${calculateAttendanceRate(getValue(), 14)}%`,
  }),
  columnHelper.accessor('classroomTeacher', {
    id: 'classroomTeacher',
    header: 'In-room staff',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor((row) => getSchoolPortalStudentIdDisplay(row), {
    id: 'studentId',
    header: 'Student ID',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'all-attended-dates',
    header: 'All attended dates',
    cell: ({ getValue }) => {
      const datesString = formatAttendedSessionDatesFromFeedback(getValue());
      if (!datesString) return '-';

      const dates = datesString.split(', ').filter((date) => date.trim() !== '');
      return <TableCellWithPopover items={dates} />;
    },
  }),
];

const csvHeaderOverrides: Record<string, string> = {
  attendedSessionsOverall: 'Attended sessions (Overall)',
  attendancePercentOverall: 'Attendance % (Overall)',
  attendancePercentPast14Days: 'Attendance % (Past 14 days)',
  'all-attended-dates': 'All attended dates',
};

const csvColumnOverrides: Record<string, (row: SchoolPortalStudent, index: number) => CsvCellValue> = {
  status: (row) => getStudentStatusLabel(row.status),
  grade: (row) => studentYearOption(row.grade),
  requestedClassTime: (row) => (row.requestedClassTime ? formatStudentClassTimeAbbr(row.requestedClassTime) : ''),
  attendedSessionsOverall: (row) =>
    row.sessionFeedbacks?.filter((feedback) => feedback.studentAttended === true && feedback.status === 'completed')
      ?.length ?? 0,
  attendancePercentOverall: (row) => `${calculateAttendanceRate(row.sessionFeedbacks)}%`,
  attendancePercentPast14Days: (row) => `${calculateAttendanceRate(row.sessionFeedbacks, 14)}%`,
  studentId: (row) => getSchoolPortalStudentIdDisplay(row) ?? '',
  'all-attended-dates': (row) => formatAttendedSessionDatesFromFeedback(row.sessionFeedbacks) || '',
};

export interface SchoolStudentsTableProps {
  schoolName?: string;
}

export function SchoolStudentsTable({ schoolName }: SchoolStudentsTableProps) {
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userQuery, activeUserQuery], setUserQuery],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      classTimeIds: [[selectedClassTimeIds, activeClassTimeIds], setSelectedClassTimeIds],
      classroomTeachers: [[selectedClassroomTeachers, activeClassroomTeachers], setSelectedClassroomTeachers],
    },
    resetFilters,
  } = usePaginatedFilters(schoolStudentsRoute.id, ['query', 'grades', 'statuses', 'classTimeIds', 'classroomTeachers']);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(schoolStudentsRoute.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetSchoolPortalStudentsSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);
  const effectiveSortBy = sortBy ?? ['attendancePercentOverall-asc'];

  const filters = useMemo(
    () => ({
      query: activeUserQuery || undefined,
      grades: activeSelectedGrades,
      statuses: activeSelectedStatuses,
      classTimeIds: activeClassTimeIds,
      classroomTeachers: activeClassroomTeachers,
    }),
    [activeUserQuery, activeSelectedGrades, activeSelectedStatuses, activeClassTimeIds, activeClassroomTeachers],
  );

  const { data, isPending, error, refetch } = useQuery(
    getSchoolPortalStudents({
      pageIndex,
      pageSize,
      filters,
      sortBy: effectiveSortBy,
    }),
  );
  useErrorToast(error, 'Failed to load students');

  const { refetch: refetchAllStudents } = useQuery({
    ...getSchoolPortalStudents({
      ...allPagesModifier,
      filters,
      sortBy: effectiveSortBy,
    }),
    enabled: false,
  });

  const students = data?.students ?? EmptyArray;
  const classTimeOptions = useMemo(
    () => data?.filterOptions.classTimes.map(({ id, label }) => ({ value: id, label })) ?? EmptyArray,
    [data?.filterOptions.classTimes],
  );
  const classroomTeacherOptions = useMemo(
    () => data?.filterOptions.classroomTeachers.map((teacher) => ({ value: teacher, label: teacher })) ?? EmptyArray,
    [data?.filterOptions.classroomTeachers],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'rowNumber',
        header: '',
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
      }),
      ...dataColumns,
    ],
    [pageIndex, pageSize],
  );

  const table = useReactTable({
    data: students,
    columns,
    state: { pagination: { pageIndex, pageSize }, sorting },
    rowCount: data?.total ?? 0,
    onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    ...tableSortingOptions,
  });

  const exportColumns = useMemo(
    () =>
      buildClientExportColumns(dataColumns, csvColumnOverrides).map((col) => ({
        ...col,
        id: csvHeaderOverrides[col.id] ?? col.id,
      })),
    [],
  );

  const handleDownloadCsv = useCallback(async () => {
    setIsDownloadingCsv(true);
    try {
      const result = await refetchAllStudents();
      const allStudents = result.data?.students ?? [];
      const date = Temporal.Now.plainDateISO().toString();
      const filename = schoolName
        ? `${schoolName.replace(/ /g, '_')}_students_export_${date}.csv`
        : `students_export_${date}.csv`;
      exportToCSV(allStudents, exportColumns, filename);
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [refetchAllStudents, exportColumns, schoolName]);

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name, email or state ID"
          value={userQuery ?? ''}
          onChange={(event) => setUserQuery(event.target.value)}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Status"
          options={getStudentStatusOptions().filter((status) => status.value !== StudentStatus.program_concluded)}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterDropdown
          title="Class time"
          options={classTimeOptions}
          selectedValues={selectedClassTimeIds ?? []}
          onChange={setSelectedClassTimeIds}
        />
        <TableFilterDropdown
          title="In-room staff"
          options={classroomTeacherOptions}
          selectedValues={selectedClassroomTeachers ?? []}
          onChange={setSelectedClassroomTeachers}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userQuery,
      setUserQuery,
      selectedGrades,
      setSelectedGrades,
      selectedStatuses,
      setSelectedStatuses,
      classTimeOptions,
      selectedClassTimeIds,
      setSelectedClassTimeIds,
      classroomTeacherOptions,
      selectedClassroomTeachers,
      setSelectedClassroomTeachers,
      resetFilters,
    ],
  );

  const topBarRightComponents = (
    <Button
      className="ml-auto h-8"
      disabled={isPending || !!error || isDownloadingCsv}
      onClick={handleDownloadCsv}
      data-testid="school-students-download-csv"
    >
      Download as CSV
      {isDownloadingCsv ? <LoaderIcon className="ml-2 h-4 w-4 animate-spin" /> : null}
    </Button>
  );

  return (
    <EntityTable
      table={table}
      showPagination
      isLoading={isPending}
      hasError={!!error}
      emptyState="No students found"
      errorState={<TableErrorState refetchAction={refetch} />}
      filters={{ filterLeftComponents }}
      filterLeftClassName="flex-wrap"
      topBarRightComponents={topBarRightComponents}
      headerClassName="bg-[#F3F4F6]"
      paginationLeftComponents={
        <span className="text-sm font-medium text-foreground" data-testid="school-students-total">
          Total students: {data?.total ?? 0}
        </span>
      }
    />
  );
}
